"use client";

import { useState } from "react";
import Link from "@/components/locale-link";
import {
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  elementText,
  FrameworkElements,
} from "@/components/tools/research-discovery/framework-elements";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  builderQuery,
  buildQuestion,
  type ElementInput,
  elementTerms,
  emptyInput,
  FRAMEWORKS,
  type Framework,
  type FrameworkId,
  type FrameworkInput,
  frameworkFor,
  searchKeys,
  searchString,
} from "@/lib/tool-kit/research-discovery/question-frameworks";
import { genericQuery } from "@/lib/tool-kit/research-workflow/boolean";

/** English strings. A translated content file overrides these through `ui`. */
export const SPIDER_QUESTION_GENERATOR_STRINGS = {
  frameworkLabel: "Framework",
  names: {
    spider: "SPIDER",
    spice: "SPICE",
    peo: "PEO",
    pcc: "PCC",
    eclipse: "ECLIPSE",
  },
  expansions: {
    spider: "Sample, Phenomenon of Interest, Design, Evaluation, Research type",
    spice: "Setting, Perspective, Intervention, Comparison, Evaluation",
    peo: "Population, Exposure, Outcome",
    pcc: "Population, Concept, Context",
    eclipse:
      "Expectation, Client group, Location, Impact, Professionals, SErvice",
  },
  uses: {
    spider:
      "Qualitative and mixed-methods evidence synthesis: questions about experiences, views and perceptions.",
    spice:
      "Social science, education and service evaluation: a question about a setting, a perspective, and how something is judged.",
    peo: "Questions about an exposure or experience and what follows from it, where no one chose the exposure. Also used for qualitative questions about how people experience something.",
    pcc: "Scoping reviews, which map what is known about a concept in a population and context rather than answer a narrow effect question.",
    eclipse:
      "Health policy and service-management questions about a service, the people it serves and the change you want.",
  },
  sources: {
    spider: "Cooke, Smith and Booth, Qualitative Health Research, 2012",
    spice: "Booth, Library Hi Tech, 2006",
    peo: "As used in the Joanna Briggs Institute Manual for Evidence Synthesis",
    pcc: "Joanna Briggs Institute Manual for Evidence Synthesis, scoping reviews",
    eclipse:
      "Wildridge and Bell, Health Information and Libraries Journal, 2002",
  },
  elements: {
    spider: {
      s: {
        letter: "S",
        name: "Sample",
        prompt: "Who is being studied?",
        example: "e.g. women undergoing IVF treatment",
      },
      pi: {
        letter: "PI",
        name: "Phenomenon of Interest",
        prompt: "The experience, behaviour or decision you want to understand.",
        example: "e.g. treatment decisions",
      },
      d: {
        letter: "D",
        name: "Design",
        prompt: "How the studies gathered data.",
        example: "e.g. interviews, focus groups",
      },
      e: {
        letter: "E",
        name: "Evaluation",
        prompt: "The outcomes you care about: views, attitudes, experiences.",
        example: "e.g. experiences, perceptions",
      },
      r: {
        letter: "R",
        name: "Research type",
        prompt: "The kinds of study to include.",
        example: "e.g. qualitative, mixed methods",
      },
    },
    spice: {
      s: {
        letter: "S",
        name: "Setting",
        prompt: "Where does this take place?",
        example: "e.g. primary care clinics",
      },
      p: {
        letter: "P",
        name: "Perspective",
        prompt: "For whom, or from whose point of view?",
        example: "e.g. adult patients with diabetes",
      },
      i: {
        letter: "I",
        name: "Intervention or interest",
        prompt: "What is done, or what are you interested in?",
        example: "e.g. telehealth follow-up",
      },
      c: {
        letter: "C",
        name: "Comparison",
        prompt: "Compared with what, if anything?",
        example: "e.g. in-person follow-up",
      },
      e: {
        letter: "E",
        name: "Evaluation",
        prompt: "With what result, or how is success judged?",
        example: "e.g. patient satisfaction",
      },
    },
    peo: {
      p: {
        letter: "P",
        name: "Population",
        prompt: "Who are the people or the problem?",
        example: "e.g. hospital nurses",
      },
      e: {
        letter: "E",
        name: "Exposure",
        prompt: "The exposure, experience or condition they have.",
        example: "e.g. night-shift work",
      },
      o: {
        letter: "O",
        name: "Outcome",
        prompt: "The outcome, or the experience that follows.",
        example: "e.g. burnout",
      },
    },
    pcc: {
      p: {
        letter: "P",
        name: "Population",
        prompt: "The people, with any characteristics that matter.",
        example: "e.g. older adults",
      },
      concept: {
        letter: "C",
        name: "Concept",
        prompt: "The idea the review maps.",
        example: "e.g. loneliness interventions",
      },
      context: {
        letter: "C",
        name: "Context",
        prompt: "The setting, culture or geography, if limited.",
        example: "e.g. residential care",
      },
    },
    eclipse: {
      e: {
        letter: "E",
        name: "Expectation",
        prompt: "The improvement, information or innovation sought.",
        example: "e.g. shorter waiting times",
      },
      c: {
        letter: "C",
        name: "Client group",
        prompt: "At whom is the service aimed?",
        example: "e.g. older adults with falls",
      },
      l: {
        letter: "L",
        name: "Location",
        prompt: "Where is the service?",
        example: "e.g. rural hospitals",
      },
      i: {
        letter: "I",
        name: "Impact",
        prompt: "What would success look like?",
        example: "e.g. fewer missed appointments",
      },
      p: {
        letter: "P",
        name: "Professionals",
        prompt: "Who provides or improves the service?",
        example: "e.g. physiotherapists",
      },
      se: {
        letter: "SE",
        name: "Service",
        prompt: "Which service are you asking about?",
        example: "e.g. an outpatient falls clinic",
      },
    },
  },
  questions: {
    spider: "What are the experiences of {s} regarding {pi}?",
    spice: "For {p} in {s}, what is the effect of {i}{c} on {e}?",
    peo: "What is the association between {e} and {o} among {p}?",
    pcc: "What is known about {concept} among {p}{context}?",
    eclipse: "How can {se} for {c} in {l}, delivered by {p}, achieve {e}{i}?",
  },
  clauses: {
    spice: { c: " compared with {value}" },
    pcc: { context: " in {value}" },
    eclipse: { i: " and {value}" },
  },
  missing: "[{name}]",
  optionalNote: "Optional: left out of the question when empty.",
  termsLabel:
    "Search terms for {element} — keywords and synonyms, one per line",
  termsHint: "Leave blank to search for the element as written above.",
  termsPlaceholder: "e.g. women\nfemale*\nIVF patients",
  includeLabel: "Use in the search string",
  elementsHeading: "Your {framework} elements",
  questionHeading: "Your draft {framework} question",
  questionNote:
    "A draft to edit, not a finished question. Check that it can be answered by the kind of study you expect to find.",
  summaryHeading: "The elements as a list",
  summaryColumns: { element: "Element", yours: "Your words" },
  searchHeading: "Search terms",
  blockColumn: "Search block",
  searchStringLabel: "Combined search string",
  emptySearch:
    "Fill in an element marked “Use in the search string” to see its search block.",
  builderLink:
    "Turn these terms into PubMed, Scopus, Web of Science and other database strings in the Boolean Search String Builder",
  picoLink:
    "For a clinical question about an intervention, use the PICO Question Generator",
  copy: "Copy",
  copied: "Copied!",
  empty: "–",
} as const;

export type SpiderQuestionGeneratorStrings =
  typeof SPIDER_QUESTION_GENERATOR_STRINGS;

type Strings = SpiderQuestionGeneratorStrings;

function phrases(strings: Strings, framework: Framework) {
  const clauses =
    (strings.clauses as Record<string, Record<string, string>>)[framework.id] ??
    {};
  const missing = Object.fromEntries(
    framework.keys.map((key) => [
      key,
      t(strings.missing, { name: elementText(strings, framework, key).name }),
    ])
  );
  return { question: strings.questions[framework.id], clauses, missing };
}

function summaryText(
  strings: Strings,
  framework: Framework,
  input: FrameworkInput
): string {
  return framework.keys
    .map((key) => {
      const text = elementText(strings, framework, key);
      return `${text.letter} — ${text.name}: ${input[key]?.value.trim() || strings.empty}`;
    })
    .join("\n");
}

export default function SpiderQuestionGeneratorWidget() {
  const strings = useToolUi(SPIDER_QUESTION_GENERATOR_STRINGS);
  const [id, setId] = useState<FrameworkId>("spider");
  const [inputs, setInputs] = useState<Record<string, FrameworkInput>>(() =>
    Object.fromEntries(FRAMEWORKS.map((f) => [f.id, emptyInput(f)]))
  );
  const framework = frameworkFor(id);
  const input = inputs[id];
  const name = strings.names[id];
  const question = buildQuestion(framework, input, phrases(strings, framework));
  const search = searchString(framework, input);
  const keys = searchKeys(framework, input);
  const link = builderQuery(framework, input);

  function change(key: string, next: ElementInput) {
    setInputs((current) => ({
      ...current,
      [id]: { ...current[id], [key]: next },
    }));
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.frameworkLabel}
        name="sqg-framework"
        onChange={setId}
        options={FRAMEWORKS.map((f) => ({
          value: f.id,
          label: strings.names[f.id],
        }))}
        value={id}
      />
      <div className={`${CARD} space-y-1`}>
        <p className="font-medium text-foreground">
          {name} — {strings.expansions[id]}
        </p>
        <p className="text-muted-foreground text-sm">{strings.uses[id]}</p>
        <p className={HINT}>{strings.sources[id]}</p>
      </div>

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>
          {t(strings.elementsHeading, { framework: name })}
        </h2>
        <FrameworkElements
          framework={framework}
          input={input}
          onChange={change}
          strings={strings}
        />
      </section>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>
          {t(strings.questionHeading, { framework: name })}
        </h2>
        <p className="font-medium text-foreground text-lg leading-relaxed">
          {question}
        </p>
        <p className={HINT}>{strings.questionNote}</p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          small
          text={question}
        />
      </section>

      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.summaryHeading}</h3>
        <DataTable
          head={[strings.summaryColumns.element, strings.summaryColumns.yours]}
          rows={framework.keys.map((key) => {
            const text = elementText(strings, framework, key);
            return [
              `${text.letter} — ${text.name}`,
              input[key]?.value.trim() || strings.empty,
            ];
          })}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          small
          text={summaryText(strings, framework, input)}
        />
      </section>

      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.searchHeading}</h3>
        {keys.length === 0 ? (
          <Notice>{strings.emptySearch}</Notice>
        ) : (
          <>
            <DataTable
              head={[strings.summaryColumns.element, strings.blockColumn]}
              rows={keys.map((key) => [
                elementText(strings, framework, key).name,
                genericQuery([elementTerms(input, key)]),
              ])}
            />
            <div className={`${CARD} space-y-3`}>
              <p className="font-medium text-foreground text-sm">
                {strings.searchStringLabel}
              </p>
              <p className="break-words font-mono text-foreground text-sm">
                {search}
              </p>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                small
                text={search}
              />
            </div>
          </>
        )}
        <p className="text-sm">
          <Link
            className="font-medium text-primary underline underline-offset-2"
            href={
              link
                ? `/tools/boolean-search-builder?${link}`
                : "/tools/boolean-search-builder"
            }
          >
            {strings.builderLink}
          </Link>
        </p>
        <p className="text-sm">
          <Link
            className="font-medium text-primary underline underline-offset-2"
            href="/tools/pico-question-generator"
          >
            {strings.picoLink}
          </Link>
        </p>
      </section>
    </div>
  );
}
